import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import {
  createClassroom,
  getClassrooms,
  updateClassroom,
  deleteClassroom
} from '../firebase/lessonPlannerService';
import DashboardLayout from './DashboardLayout';
import './Classrooms.css';

function Classrooms() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    gradeLevel: '',
    period: ''
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadClassrooms();
  }, [currentUser, navigate]);

  const loadClassrooms = async () => {
    setLoading(true);
    const result = await getClassrooms(currentUser.id);
    if (result.success) {
      setClassrooms(result.classrooms);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingClassroom) {
      const result = await updateClassroom(editingClassroom.id, formData);
      if (result.success) {
        loadClassrooms();
        closeModal();
      }
    } else {
      const result = await createClassroom(currentUser.id, formData);
      if (result.success) {
        loadClassrooms();
        closeModal();
      }
    }
  };

  const handleEdit = (classroom) => {
    setEditingClassroom(classroom);
    setFormData({
      name: classroom.name,
      subject: classroom.subject || '',
      gradeLevel: classroom.gradeLevel || '',
      period: classroom.period || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (classroomId) => {
    if (window.confirm('Are you sure you want to delete this classroom?')) {
      const result = await deleteClassroom(classroomId);
      if (result.success) {
        loadClassrooms();
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClassroom(null);
    setFormData({
      name: '',
      subject: '',
      gradeLevel: '',
      period: ''
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading classrooms...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="classrooms-container">
        <div className="page-header">
          <div>
            <h1>Classrooms</h1>
            <p className="subtitle">Manage your classrooms</p>
          </div>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus size={20} />
            New Classroom
          </button>
        </div>

        {classrooms.length === 0 ? (
          <div className="empty-state">
            <Users size={64} className="empty-icon" />
            <h3>No classrooms yet</h3>
            <p>Create your first classroom to organize your lessons</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={20} />
              Create Classroom
            </button>
          </div>
        ) : (
          <div className="classrooms-grid">
            {classrooms.map(classroom => (
              <div key={classroom.id} className="classroom-card">
                <div className="card-header">
                  <Users className="classroom-icon" size={32} />
                  <div className="card-actions">
                    <button onClick={() => handleEdit(classroom)} className="btn-icon">
                      <Edit size={18} />
                    </button>
                    <button onClick={() => handleDelete(classroom.id)} className="btn-icon delete">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <h3>{classroom.name}</h3>

                <div className="classroom-details">
                  <div className="detail-row">
                    <span className="detail-label">Subject:</span>
                    <span className="detail-value">{classroom.subject}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Grade Level:</span>
                    <span className="detail-value">{classroom.gradeLevel}</span>
                  </div>
                  {classroom.period && (
                    <div className="detail-row">
                      <span className="detail-label">Period:</span>
                      <span className="detail-value">{classroom.period}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingClassroom ? 'Edit Classroom' : 'Create New Classroom'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Classroom Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Room 301 - Algebra"
                  />
                </div>

                <div className="form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    placeholder="e.g., Mathematics"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Grade Level *</label>
                    <input
                      type="text"
                      value={formData.gradeLevel}
                      onChange={(e) => setFormData({ ...formData, gradeLevel: e.target.value })}
                      required
                      placeholder="e.g., 9th Grade"
                    />
                  </div>

                  <div className="form-group">
                    <label>Period</label>
                    <input
                      type="text"
                      value={formData.period}
                      onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                      placeholder="e.g., 3rd Period"
                    />
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingClassroom ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Classrooms;
